<template>
  <div class="ptm-home">
    <!-- Header Navigation -->
    <header class="header">
      <div class="nav-container">
        <div class="logo-section">
          <img
            src="../assets/jingjie_logo.png"
            alt="PTM Bio Logo"
            class="logo-icon"
          />
          <div class="logo-text">PTM BIO Platform</div>
        </div>
        <nav>
          <ul class="nav-menu">
            <li><a href="#home" @click.prevent="setSection('home')">Home</a></li>
            <li><a href="#features" @click.prevent="setSection('features')">Features</a></li>
            <li><a href="#contact" @click.prevent="setSection('contact')">Contacts</a></li>
          </ul>
        </nav>
        <div class="nav-actions">
          <a href="javascript:void(0)" class="login-btn" @click="goToAuth('login')">
            Login / Register
          </a>
        </div>
      </div>
    </header>

    <!-- Main Container -->
    <div class="main-container">
      <!-- Protein Structure Background (AlphaFold-style ribbon) -->
      <div class="protein-container" aria-hidden="true">
        <div class="protein-png protein-png--1"></div>
        <div class="protein-png protein-png--2"></div>
      </div>

      <!-- Floating Background Elements -->
      <div class="floating-element float-1"></div>
      <div class="floating-element float-2"></div>
      <div class="floating-element float-3"></div>
      <div class="floating-element float-4"></div>

      <!-- Hero Section -->
      <section class="hero-section" v-show="currentSection === 'home'">
        <div class="hero-grid">
          <div class="hero-left">
            <div class="hero-content">
              <h1>Advancing Proteomics Research</h1>
              <p>Comprehensive data analysis platform for proteomics research, enabling breakthrough discoveries in biomedical science</p>
            </div>

            <div class="hero-buttons">
              <a href="javascript:void(0)" class="btn-primary" @click="goToAuth('signup')">Start Now</a>
              <a href="#features" class="btn-secondary" @click.prevent="setSection('features')">Learn More</a>
            </div>
          </div>

          <!-- Platform Statistics (right side on desktop) -->
          <div class="hero-right">
            <div class="hero-stats" ref="heroStats">
              <div class="hero-stat-card stat-card-1">
                <div class="hero-stat-number" :data-target="stats.projects">0</div>
                <div class="hero-stat-label">Projects</div>
              </div>
              <div class="hero-stat-card stat-card-2">
                <div class="hero-stat-number" :data-target="stats.samples">0</div>
                <div class="hero-stat-label">Samples</div>
              </div>
              <div class="hero-stat-card stat-card-3">
                <div class="hero-stat-number" :data-target="stats.genes">0</div>
                <div class="hero-stat-label">Proteins</div>
              </div>
              <div class="hero-stat-card stat-card-4">
                <div class="hero-stat-number" :data-target="stats.datasets">0</div>
                <div class="hero-stat-label">Datasets</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <!-- Auth Section -->
      <section v-show="currentSection === 'auth'" class="content-section">
        <div style="display: flex; justify-content: center; align-items: center; width: 100%; height: 100%;">
          <div style="width: 340px;">
            <!-- Login Card -->
            <div id="login-card" class="auth-card" :class="{ active: activeCard === 'login' }">
              <h2>Login</h2>
              <form @submit.prevent="handleLogin">
                <div class="form-group">
                  <label>Username</label>
                  <input type="text" v-model="loginForm.username" placeholder="Enter your username" />
                </div>
                <div class="form-group">
                  <label>Password</label>
                  <input type="password" v-model="loginForm.password" placeholder="Enter your password" />
                </div>
                <div class="remember-me">
                  <label>
                    <input type="checkbox" v-model="rememberMe" />
                    Remember me
                  </label>
                </div>
                <el-alert
                  v-if="authError"
                  :title="authError"
                  type="error"
                  :closable="true"
                  @close="authError = ''"
                  style="margin-bottom: 20px;">
                </el-alert>
                <el-alert
                  v-if="emailNotVerified"
                  title="Email Not Verified"
                  type="warning"
                  description="Please verify your email address before logging in."
                  :closable="false"
                  style="margin-bottom: 20px;">
                  <el-button
                    size="small"
                    type="primary"
                    @click="handleResendVerification"
                    :loading="resendLoading"
                    style="margin-top: 10px;">
                    {{ resendLoading ? 'Sending...' : 'Resend Verification Email' }}
                  </el-button>
                </el-alert>
                <button type="submit" class="login-submit" :disabled="authLoading">
                  {{ authLoading ? 'Logging in...' : 'Login' }}
                </button>
              </form>
              <div class="login-footer">
                Don't have an account? <a @click="switchCard('signup')">Sign up</a><br>
                <a @click="switchCard('forgot')">Forgot password?</a>
              </div>
            </div>

            <!-- Sign Up Card -->
            <div id="signup-card" class="auth-card" :class="{ active: activeCard === 'signup' }">
              <div class="back-to-login" @click="switchCard('login')">
                <span>&larr;</span> Back to Login
              </div>
              <h2>Register</h2>
              <form @submit.prevent="handleRegister">
                <div class="form-group">
                  <label>Username *</label>
                  <input type="text" v-model="registerForm.username" placeholder="Enter your username" required />
                </div>
                <div class="form-group">
                  <label>Email Address *</label>
                  <input type="email" v-model="registerForm.email" placeholder="Enter your email" required />
                  <small style="color: #666; font-size: 12px;">Required for account verification</small>
                </div>
                <div class="form-group">
                  <label>Password *</label>
                  <input type="password" v-model="registerForm.password" placeholder="Create a password (min 6 characters)" required />
                </div>
                <div class="form-group">
                  <label>Confirm Password *</label>
                  <input type="password" v-model="registerForm.confirmPassword" placeholder="Confirm your password" required />
                </div>
                <div class="terms-agree">
                  <label>
                    <input type="checkbox" v-model="agreeTerms" />
                    <span>I agree to the <a href="#">Terms of Service</a> and <a href="#">Privacy Policy</a></span>
                  </label>
                </div>
                <el-alert
                  v-if="authError"
                  :title="authError"
                  type="error"
                  :closable="true"
                  @close="authError = ''"
                  style="margin-bottom: 20px;">
                </el-alert>
                <el-alert
                  v-if="registrationSuccess"
                  title="Registration Successful!"
                  type="success"
                  description="Please check your email to verify your account before logging in."
                  :closable="false"
                  style="margin-bottom: 20px;">
                </el-alert>
                <button type="submit" class="login-submit" :disabled="authLoading">
                  {{ authLoading ? 'Creating account...' : 'Sign Up' }}
                </button>
              </form>
              <div class="login-footer">
                Already have an account? <a @click="switchCard('login')">Login</a>
              </div>
            </div>

            <!-- Forgot Password Card -->
            <div id="forgot-card" class="auth-card" :class="{ active: activeCard === 'forgot' }">
              <div class="back-to-login" @click="switchCard('login')">
                <span>&larr;</span> Back to Login
              </div>
              <h2>Reset Password</h2>
              <p class="forgot-desc">Enter your email address or username and we'll send you a link to reset your password.</p>
              <form @submit.prevent="handleForgotPassword">
                <div class="form-group">
                  <label>Email or Username</label>
                  <input type="text" v-model="forgotPasswordForm.email" placeholder="Enter your email or username" />
                </div>
                <el-alert
                  v-if="authError"
                  :title="authError"
                  type="error"
                  :closable="true"
                  @close="authError = ''"
                  style="margin-bottom: 20px;">
                </el-alert>
                <el-alert
                  v-if="forgotPasswordSuccess"
                  title="Email Sent!"
                  type="success"
                  description="If an account exists with that email, a password reset link has been sent."
                  :closable="false"
                  style="margin-bottom: 20px;">
                </el-alert>
                <button type="submit" class="login-submit" :disabled="authLoading">
                  {{ authLoading ? 'Sending...' : 'Send Reset Link' }}
                </button>
              </form>
              <div class="login-footer" style="margin-top: 30px;">
                Remember your password? <a @click="switchCard('login')">Login</a><br>
                Don't have an account? <a @click="switchCard('signup')">Sign up</a>
              </div>
            </div>
          </div>
        </div>
      </section>

      <!-- Features Section -->
      <section v-show="currentSection === 'features'" class="content-section">
        <div class="content-inner">
          <h2 class="content-title">Key Features</h2>
          <p class="content-subtitle">Core capabilities for proteomics research</p>
          <div class="feature-cards">
            <div class="feature-card" v-for="item in featureList" :key="item.title">
              <div class="feature-icon">{{ item.icon }}</div>
              <h3>{{ item.title }}</h3>
              <p>{{ item.desc }}</p>
            </div>
          </div>
        </div>
      </section>

      <!-- Contact Section -->
      <section v-show="currentSection === 'contact'" class="content-section">
        <div class="content-inner contact">
          <h2 class="content-title">Get in Touch</h2>
          <p class="content-subtitle">We'd love to hear from you</p>
          <div class="contact-grid">
            <div class="contact-item">
              <strong>Email</strong>
              <span>info@proteomics.com</span>
            </div>
            <div class="contact-item">
              <strong>Phone</strong>
              <span>+1 (555) 123-4567</span>
            </div>
            <div class="contact-item">
              <strong>Address</strong>
              <span>123 Science Park, CA 94025</span>
            </div>
          </div>
        </div>
      </section>

      <!-- Bottom Perspective Graphics -->
      <div class="perspective-bg">
        <div class="perspective-grid"></div>
        <div class="perspective-glow"></div>
      </div>

      <!-- Footer Bar -->
      <div class="footer-bar">
        <p>&copy; 2024 Proteomics Platform. All rights reserved.</p>
        <div class="footer-links">
          <a href="#">Privacy Policy</a>
          <a href="#">Terms of Service</a>
          <a href="#">Contact Us</a>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios'

export default {
  name: 'FrontPage',
  data() {
    return {
      activeCard: 'login',
      statsAnimated: false,
      rememberMe: false,
      agreeTerms: false,
      currentSection: 'home',
      featureList: [
        { icon: '📊', title: 'Differential Expression', desc: 'Identify significantly changed proteins with rich visuals.' },
        { icon: '🧬', title: 'Marker Discovery', desc: 'Find biomarkers with comparative analytics.' },
        { icon: '🔬', title: 'Functional Annotation', desc: 'GO/KEGG/Reactome-driven pathway insights.' },
        { icon: '📈', title: 'Clinical Correlation', desc: 'Relate protein signals with clinical variables.' }
      ],
      stats: {
        projects: 0,
        samples: 0,
        genes: 0,
        datasets: 0
      },
      loginForm: {
        username: '',
        password: ''
      },
      registerForm: {
        username: '',
        email: '',
        password: '',
        confirmPassword: ''
      },
      forgotPasswordForm: {
        email: ''
      },
      authError: '',
      authLoading: false,
      emailNotVerified: false,
      resendLoading: false,
      registrationSuccess: false,
      forgotPasswordSuccess: false
    }
  },
  methods: {
    switchCard(cardName) {
      this.activeCard = cardName
      this.authError = ''
      this.emailNotVerified = false
      this.registrationSuccess = false
      this.forgotPasswordSuccess = false
    },
    setSection(section) {
      this.currentSection = section
    },
    goToAuth(card = 'login') {
      this.currentSection = 'auth'
      this.switchCard(card)
    },
    async fetchStats() {
      try {
        const res = await axios.get('/api/projectSearch/platform_stats')
        if (res && res.data && res.data.code === 200 && res.data.data) {
          this.stats = {
            projects: res.data.data.projects || 0,
            samples: res.data.data.samples || 0,
            genes: res.data.data.genes || 0,
            datasets: res.data.data.datasets || 0
          }
        }
      } catch (e) {
        // fail silently; keep defaults
      } finally {
        this.$nextTick(() => this.animateNumbers())
      }
    },
    async handleLogin() {
      if (!this.loginForm.username || !this.loginForm.password) {
        this.authError = 'Please enter username and password'
        return
      }
      
      this.authLoading = true
      this.authError = ''
      this.emailNotVerified = false
      
      try {
        const response = await axios.post('/api/user/login', {
          username: this.loginForm.username,
          password: this.loginForm.password
        })
        
        if (response && response.data && response.data.code === 200) {
          this.$message.success('Login successful!')
          this.loginForm = { username: '', password: '' }
          // Redirect to dashboard after login (full reload to update app state)
          window.location.href = '/dashboard'
        } else if (response && response.data && response.data.code === 403) {
          // Email not verified
          this.emailNotVerified = true
          this.authError = ''
        } else {
          this.authError = response.data.error || 'Login failed'
        }
      } catch (error) {
        console.error('Login error:', error)
        if (error.response && error.response.data && error.response.data.code === 403) {
          this.emailNotVerified = true
          this.authError = ''
        } else {
          this.authError = 'Failed to login. Please try again.'
        }
      } finally {
        this.authLoading = false
      }
    },
    async handleRegister() {
      if (!this.registerForm.username || !this.registerForm.password || !this.registerForm.email) {
        this.authError = 'Please fill in all required fields'
        return
      }
      
      if (this.registerForm.password.length < 6) {
        this.authError = 'Password must be at least 6 characters'
        return
      }
      
      if (this.registerForm.password !== this.registerForm.confirmPassword) {
        this.authError = 'Passwords do not match'
        return
      }
      
      if (!this.agreeTerms) {
        this.authError = 'Please agree to the Terms of Service and Privacy Policy'
        return
      }
      
      this.authLoading = true
      this.authError = ''
      this.registrationSuccess = false
      
      try {
        const response = await axios.post('/api/user/register', {
          username: this.registerForm.username,
          email: this.registerForm.email,
          password: this.registerForm.password
        })
        
        if (response && response.data && response.data.code === 200) {
          this.registrationSuccess = true
          this.$message.success('Registration successful! Please check your email to verify your account.')
          // Clear form and switch to login after a short delay
          this.registerForm = {
            username: '',
            email: '',
            password: '',
            confirmPassword: ''
          }
          this.agreeTerms = false
          // Auto switch to login after 2 seconds
          setTimeout(() => {
            this.switchCard('login')
            this.registrationSuccess = false
          }, 2000)
        } else {
          this.authError = response.data.error || 'Registration failed'
        }
      } catch (error) {
        console.error('Register error:', error)
        this.authError = error.response?.data?.error || 'Failed to register. Please try again.'
      } finally {
        this.authLoading = false
      }
    },
    async handleForgotPassword() {
      if (!this.forgotPasswordForm.email) {
        this.authError = 'Please enter your email or username'
        return
      }
      
      this.authLoading = true
      this.authError = ''
      this.forgotPasswordSuccess = false
      
      try {
        const response = await axios.post('/api/user/forgot-password', {
          email: this.forgotPasswordForm.email
        })
        
        if (response && response.data && response.data.code === 200) {
          this.forgotPasswordSuccess = true
          this.$message.success('Reset link sent! Check your email.')
        } else if (response && response.data && response.data.code === 429) {
          this.authError = 'Please wait before requesting another reset link'
        } else {
          this.authError = response.data.error || 'Failed to send reset link'
        }
      } catch (error) {
        console.error('Forgot password error:', error)
        if (error.response && error.response.data && error.response.data.code === 429) {
          this.authError = 'Please wait before requesting another reset link'
        } else {
          this.authError = 'Failed to send reset link. Please try again.'
        }
      } finally {
        this.authLoading = false
      }
    },
    async handleResendVerification() {
      if (!this.loginForm.username) {
        this.$message.warning('Please enter your username first')
        return
      }
      
      this.resendLoading = true
      
      try {
        const response = await axios.post('/api/user/resend-verification', {
          username: this.loginForm.username
        })
        
        if (response && response.data && response.data.code === 200) {
          this.$message.success('Verification email sent! Check your inbox.')
          this.emailNotVerified = false
        } else if (response && response.data && response.data.code === 429) {
          this.$message.warning('Please wait before requesting another email')
        } else {
          this.$message.error(response.data.error || 'Failed to send email')
        }
      } catch (error) {
        console.error('Resend verification error:', error)
        if (error.response && error.response.data && error.response.data.code === 429) {
          this.$message.warning('Please wait before requesting another email')
        } else {
          this.$message.error('Failed to send verification email')
        }
      } finally {
        this.resendLoading = false
      }
    },
    animateNumbers() {
      if (this.statsAnimated) return
      this.statsAnimated = true
      const container = this.$refs.heroStats
      if (!container) return
      const numbers = container.querySelectorAll('.hero-stat-number')
      numbers.forEach((numEl) => {
        const targetAttr = numEl.getAttribute('data-target')
        const target = targetAttr ? parseInt(targetAttr, 10) : 0
        let current = 0
        const steps = 50
        const increment = target / steps
        const timer = setInterval(() => {
          current += increment
          if (current >= target) {
            current = target
            clearInterval(timer)
          }
          numEl.textContent = current.toLocaleString('en-US', {
            maximumFractionDigits: 0
          })
        }, 30)
      })
    }
  },
  mounted() {
    this.fetchStats()
  }
}
</script>

<style scoped>
* {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
}

.ptm-home {
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Helvetica Neue', Arial, sans-serif;
  background-color: #0a0f1a;
  color: #333;
  height: 100vh;
  overflow: hidden;
}

/* Header Navigation */
.ptm-home .header {
  background-color: transparent;
  position: fixed;
  width: 100%;
  left: 0;
  right: 0;
  top: 0;
  z-index: 1000;
  padding: 0;
  border: none;
  box-shadow: none;
}

.ptm-home .nav-container {
  max-width: 1400px;
  width: 100%;
  margin: 0 auto;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0 20px;
  box-sizing: border-box;
}

.ptm-home .header nav {
  display: flex;
  align-items: center;
}

.ptm-home .logo-section {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 15px 0;
}

.ptm-home .logo-icon {
  width: 36px;
  height: 36px;
  border-radius: 0;
  background: none;
}

.ptm-home .logo-text {
  color: white;
  font-size: 26px;
  font-weight: 500;
}

.ptm-home .nav-menu {
  display: flex;
  gap: 30px;
  list-style: none;
  margin: 0;
  padding: 0;
}

.ptm-home .nav-menu a {
  color: rgba(255, 255, 255, 0.9);
  text-decoration: none;
  font-size: 23px;
  font-weight: 600;
  transition: color 0.2s;
}

.ptm-home .nav-menu a:hover {
  color: white;
}

.ptm-home .nav-actions {
  display: flex;
  gap: 20px;
  align-items: center;
}

.ptm-home .login-btn {
  background: rgba(255, 255, 255, 0.15);
  color: white;
  padding: 10px 24px;
  border-radius: 8px;
  border: 1px solid rgba(255, 255, 255, 0.3);
  text-decoration: none;
  font-size: 23px;
  font-weight: 500;
  transition: all 0.3s ease;
  cursor: pointer;
  backdrop-filter: blur(8px);
}

.ptm-home .login-btn:hover {
  background: rgba(255, 255, 255, 0.25);
  border-color: rgba(255, 255, 255, 0.5);
  box-shadow: 0 4px 20px rgba(255, 255, 255, 0.1);
  transform: translateY(-2px);
}

/* Main Container - Full Height */
.main-container {
  height: 100vh;
  display: flex;
  flex-direction: column;
  padding-top: 66px;
  background: linear-gradient(135deg, #0f172a 0%, #1e1b4b 30%, #312e81 60%, #1e3a5f 100%);
  position: relative;
  overflow: hidden;
}

.main-container::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: 
    radial-gradient(ellipse at 20% 20%, rgba(59, 130, 246, 0.15) 0%, transparent 50%),
    radial-gradient(ellipse at 80% 80%, rgba(139, 92, 246, 0.15) 0%, transparent 50%),
    radial-gradient(ellipse at 50% 50%, rgba(6, 182, 212, 0.1) 0%, transparent 60%);
  pointer-events: none;
}

.content-section {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 40px 20px 60px;
  background: transparent;
}

.content-inner {
  max-width: 1200px;
  width: 100%;
  background: rgba(255, 255, 255, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.15);
  border-radius: 20px;
  padding: 40px;
  color: #ecf0f1;
  box-shadow: 0 15px 40px rgba(0, 0, 0, 0.2);
  backdrop-filter: blur(12px);
}

.content-title {
  font-size: 32px;
  margin-bottom: 8px;
}

.content-subtitle {
  color: rgba(255, 255, 255, 0.85);
  margin-bottom: 24px;
}

.feature-cards {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
  gap: 16px;
}

.feature-card {
  background: rgba(255, 255, 255, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 16px;
  padding: 24px;
  color: #ecf0f1;
  transition: all 0.3s ease;
}

.feature-card:hover {
  transform: translateY(-5px);
  border-color: rgba(255, 255, 255, 0.25);
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.2);
  background: rgba(255, 255, 255, 0.12);
}

.feature-card h3 {
  margin: 12px 0 8px;
  color: #fff;
  font-weight: 600;
}

.feature-card p {
  color: rgba(255, 255, 255, 0.8);
  line-height: 1.5;
}

.feature-icon {
  font-size: 36px;
  display: inline-block;
  background: rgba(255, 255, 255, 0.1);
  padding: 12px;
  border-radius: 12px;
}

.contact {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.contact-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 12px;
}

.contact-item {
  background: rgba(255, 255, 255, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 12px;
  padding: 20px;
  transition: all 0.3s ease;
}

.contact-item:hover {
  transform: translateY(-3px);
  border-color: rgba(255, 255, 255, 0.25);
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.2);
}

.contact-item strong {
  color: #fff;
  display: block;
  margin-bottom: 8px;
}

.contact-item span {
  color: rgba(255, 255, 255, 0.75);
}

/* Hero Section - Flex Layout */
.hero-section {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px 40px;
  position: relative;
  z-index: 1;
}

.hero-grid {
  max-width: 1400px;
  width: 100%;
  display: grid;
  grid-template-columns: 1fr 450px;
  gap: 60px;
  align-items: center;
}

.hero-left {
  display: flex;
  flex-direction: column;
  gap: 30px;
  align-items: center;
  text-align: center;
}

.hero-content h1 {
  color: white;
  font-size: 48px;
  margin-bottom: 15px;
  line-height: 1.2;
  text-align: center;
}

.hero-content p {
  color: #ecf0f1;
  font-size: 18px;
  line-height: 1.6;
  max-width: 600px;
  text-align: center;
  margin: 0 auto;
}

/* Hero Stats - 2x2 Grid Layout */
.hero-stats {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 14px;
  width: 100%;
  max-width: 380px;
}

.hero-right {
  display: flex;
  align-items: center;
  justify-content: center;
}

.hero-stat-card {
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(12px);
  border-radius: 16px;
  padding: 20px 16px;
  text-align: center;
  border: 1px solid rgba(255, 255, 255, 0.15);
  transition: all 0.3s ease;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
}

.hero-stat-card:hover {
  background: rgba(255, 255, 255, 0.15);
  border-color: rgba(255, 255, 255, 0.3);
  transform: translateY(-4px);
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.15);
}

.hero-stat-number {
  font-size: 36px;
  font-weight: 600;
  color: white;
  line-height: 1;
  margin-bottom: 6px;
}

.hero-stat-label {
  font-size: 12px;
  font-weight: 400;
  color: rgba(255, 255, 255, 0.85);
  letter-spacing: 0.3px;
}

.btn-primary {
  background: #3b82f6;
  color: white;
  padding: 14px 32px;
  border-radius: 10px;
  text-decoration: none;
  font-size: 16px;
  font-weight: 600;
  transition: all 0.3s ease;
  display: inline-block;
  box-shadow: 0 4px 20px rgba(59, 130, 246, 0.35);
  border: none;
}

.btn-primary:hover {
  background: #2563eb;
  transform: translateY(-3px);
  box-shadow: 0 8px 30px rgba(59, 130, 246, 0.45);
}

.btn-secondary {
  background: rgba(255, 255, 255, 0.1);
  color: white;
  padding: 14px 32px;
  border: 2px solid rgba(255, 255, 255, 0.4);
  border-radius: 10px;
  text-decoration: none;
  font-size: 16px;
  font-weight: 600;
  transition: all 0.3s ease;
  display: inline-block;
  backdrop-filter: blur(8px);
}

.btn-secondary:hover {
  background: rgba(255, 255, 255, 0.2);
  border-color: rgba(255, 255, 255, 0.6);
  color: #fff;
  transform: translateY(-3px);
  box-shadow: 0 4px 20px rgba(255, 255, 255, 0.15);
}

.hero-buttons {
  display: flex;
  gap: 20px;
  justify-content: center;
}

/* Card Container */
.card-container {
  position: relative;
}

.auth-card {
  display: none;
  background: #ffffff;
  border-radius: 16px;
  padding: 28px 32px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  max-height: calc(100vh - 120px);
  overflow-y: auto;
  border: 1px solid #e2e8f0;
}

.auth-card.active {
  display: block;
  animation: cardFadeIn 0.4s ease;
}

@keyframes cardFadeIn {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.auth-card h2 {
  font-size: 22px;
  margin-bottom: 18px;
  color: #1e293b;
  text-align: center;
  font-weight: 700;
}

.back-to-login {
  display: flex;
  align-items: center;
  gap: 6px;
  color: #3498db;
  cursor: pointer;
  font-size: 13px;
  margin-bottom: 12px;
}

.back-to-login:hover {
  text-decoration: underline;
}

.forgot-desc {
  text-align: center;
  color: #7f8c8d;
  margin-bottom: 16px;
  font-size: 13px;
}

.form-group {
  margin-bottom: 14px;
}

.form-group label {
  display: block;
  margin-bottom: 5px;
  color: #2c3e50;
  font-weight: 500;
  font-size: 13px;
}

.form-group input {
  width: 100%;
  padding: 10px 12px;
  border: 1.5px solid #e2e8f0;
  border-radius: 8px;
  font-size: 14px;
  transition: all 0.3s ease;
  background: #f8fafc;
}

.form-group input:focus {
  outline: none;
  border-color: #3b82f6;
  background: #fff;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.15);
}

.remember-me {
  margin-bottom: 14px;
}

.remember-me label {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  cursor: pointer;
}

.remember-me input[type="checkbox"] {
  width: auto;
}

.terms-agree {
  margin-bottom: 14px;
}

.terms-agree label {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  font-size: 12px;
  line-height: 1.4;
  color: #7f8c8d;
  cursor: pointer;
}

.terms-agree input[type="checkbox"] {
  width: auto;
  margin-top: 2px;
  flex-shrink: 0;
}

.terms-agree a {
  color: #3498db;
}

.login-submit {
  width: 100%;
  background: #3b82f6;
  color: white;
  border: none;
  padding: 11px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 4px 15px rgba(59, 130, 246, 0.25);
}

.login-submit:hover {
  background: #2563eb;
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(59, 130, 246, 0.35);
}

.login-submit:disabled {
  background: #94a3b8;
  cursor: not-allowed;
  transform: none;
  box-shadow: none;
}

.login-footer {
  margin-top: 14px;
  text-align: center;
  font-size: 13px;
  color: #7f8c8d;
  line-height: 1.6;
}

.login-footer a {
  color: #3498db;
  text-decoration: none;
  cursor: pointer;
}

.login-footer a:hover {
  text-decoration: underline;
}

/* Bottom Perspective Graphics */
.perspective-bg {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  height: 200px;
  overflow: hidden;
  pointer-events: none;
}

.perspective-grid {
  position: absolute;
  bottom: -50px;
  left: -10%;
  right: -10%;
  height: 300px;
  background:
    linear-gradient(90deg, rgba(59, 130, 246, 0.08) 1px, transparent 1px),
    linear-gradient(rgba(139, 92, 246, 0.08) 1px, transparent 1px);
  background-size: 60px 60px;
  transform: perspective(500px) rotateX(60deg);
  transform-origin: center bottom;
}

.perspective-glow {
  position: absolute;
  bottom: 0;
  left: 50%;
  transform: translateX(-50%);
  width: 80%;
  height: 200px;
  background: radial-gradient(ellipse at center bottom, rgba(59, 130, 246, 0.4) 0%, rgba(139, 92, 246, 0.2) 40%, transparent 70%);
}

/* Protein ribbon background (AlphaFold-style) */
.protein-container {
  position: absolute;
  inset: 0;
  overflow: hidden;
  pointer-events: none;
  z-index: 0;
}

.protein-png {
  position: absolute;
  inset: -10%;
  background-image: url('../assets/protein1.png');
  background-repeat: no-repeat;
  background-position: 88% 50%;
  background-size: 860px auto;
  opacity: 0.24;
  filter: saturate(1.12) contrast(1.05);
  mix-blend-mode: screen;
}

.protein-png--1 {
  transform: rotate(-100deg);
  animation: proteinDrift 28s ease-in-out infinite;
}

.protein-png--2 {
  transform: rotate(20deg) scale(1.06);
  opacity: 0.14;
  background-image: url('../assets/protein2.png');
  background-position: 12% 72%;
  background-size: 760px auto;
  animation: proteinDrift2 34s ease-in-out infinite;
}

@keyframes proteinDrift {
  0% { transform: rotate(-14deg) translate3d(0, 0, 0); }
  50% { transform: rotate(-14deg) translate3d(12px, -18px, 0); }
  100% { transform: rotate(-14deg) translate3d(0, 0, 0); }
}

@keyframes proteinDrift2 {
  0% { transform: rotate(18deg) scale(1.06) translate3d(0, 0, 0); }
  50% { transform: rotate(18deg) scale(1.06) translate3d(-16px, 14px, 0); }
  100% { transform: rotate(18deg) scale(1.06) translate3d(0, 0, 0); }
}

.main-container::before {
  z-index: 0;
}

.hero-section,
.content-section {
  position: relative;
  z-index: 1;
}

@media (prefers-reduced-motion: reduce) {
  .protein-png--1,
  .protein-png--2 {
    animation: none !important;
  }
}

@media (max-width: 768px) {
  .protein-png {
    opacity: 0.16;
    background-size: 560px auto;
    background-position: 80% 18%;
    filter: saturate(1.05) contrast(1.03);
  }

  .protein-png--2 {
    opacity: 0.08;
    background-size: 520px auto;
    background-position: 18% 86%;
  }
}

/* Floating Elements */
.floating-element {
  position: absolute;
  border-radius: 50%;
  pointer-events: none;
  z-index: 1;
}

.float-1 {
  width: 350px;
  height: 350px;
  top: 5%;
  left: -120px;
  background: radial-gradient(circle, rgba(59, 130, 246, 0.15) 0%, rgba(139, 92, 246, 0.1) 50%, transparent 70%);
  animation: float 8s ease-in-out infinite;
}

.float-2 {
  width: 280px;
  height: 280px;
  bottom: 15%;
  right: -80px;
  background: radial-gradient(circle, rgba(6, 182, 212, 0.15) 0%, rgba(59, 130, 246, 0.1) 50%, transparent 70%);
  animation: float 6s ease-in-out infinite reverse;
}

.float-3 {
  width: 200px;
  height: 200px;
  top: 45%;
  left: 25%;
  background: radial-gradient(circle, rgba(139, 92, 246, 0.12) 0%, rgba(244, 114, 182, 0.08) 50%, transparent 70%);
  animation: float 10s ease-in-out infinite;
}

.float-4 {
  width: 180px;
  height: 180px;
  top: 20%;
  right: 20%;
  background: radial-gradient(circle, rgba(34, 211, 238, 0.12) 0%, rgba(59, 130, 246, 0.08) 50%, transparent 70%);
  animation: float 12s ease-in-out infinite reverse;
}

@keyframes float {
  0%, 100% { 
    transform: translateY(0) rotate(0deg) scale(1);
    opacity: 0.8;
  }
  50% { 
    transform: translateY(-30px) rotate(10deg) scale(1.05);
    opacity: 1;
  }
}

/* Footer Bar */
.footer-bar {
  background: rgba(0, 0, 0, 0.3);
  padding: 18px 40px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  position: relative;
  z-index: 1;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(8px);
}

.footer-bar p {
  color: rgba(255, 255, 255, 0.7);
  font-size: 13px;
}

.footer-links {
  display: flex;
  gap: 20px;
}

.footer-links a {
  color: rgba(255, 255, 255, 0.7);
  text-decoration: none;
  font-size: 13px;
  transition: color 0.3s;
}

.footer-links a:hover {
  color: white;
}

/* Responsive */
@media (max-width: 1024px) {
  .hero-grid {
    grid-template-columns: 1fr;
    gap: 30px;
  }

  .card-container {
    max-width: 400px;
    margin: 0 auto;
  }

  .hero-content h1 {
    font-size: 36px;
    text-align: center;
  }

  .hero-content p {
    text-align: center;
    margin: 0 auto;
  }

  .hero-right {
    justify-content: center;
  }

  .hero-stats {
    max-width: 420px;
    gap: 12px;
  }

  .hero-stat-card {
    padding: 18px 14px;
  }

  .hero-stat-number {
    font-size: 32px;
  }

  .hero-stat-label {
    font-size: 11px;
  }

  .hero-buttons {
    justify-content: center;
  }
}

@media (max-width: 768px) {
  .hero-section {
    padding: 20px;
  }

  .nav-menu {
    display: none;
  }

  .hero-stats {
    grid-template-columns: repeat(2, 1fr);
    gap: 10px;
    max-width: 100%;
  }

  .hero-stat-card {
    padding: 16px 12px;
  }

  .hero-stat-number {
    font-size: 26px;
  }

  .hero-stat-label {
    font-size: 10px;
  }

  .auth-card {
    padding: 24px 18px;
  }

  .footer-bar {
    flex-direction: column;
    gap: 10px;
    text-align: center;
  }

  .feature-cards {
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  }
  
  .float-4 {
    display: none;
  }
}
</style>
